import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { GridSearchIcon } from "@mui/x-data-grid";
import { MasterSchemeInterface } from "../../family_enquiries/family_enquiry_list";
import { getDistrictMasterData } from "../../../../services/master/master_service";
import { useQuery } from "react-query";
import { useFilterEnquiriesStore } from "../../../../store/use-scheme-enquiries-filter-store";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface FilterEnquiriesInterface {
  category: MasterSchemeInterface[];
  states: MasterSchemeInterface[];
  genderList: MasterSchemeInterface[];
  govermentList: MasterSchemeInterface[];
  loading?: boolean;
}

export default function FilterEnquiries({
  states,
  category,
  govermentList,
  genderList,
  loading = false,
}: FilterEnquiriesInterface) {

// ✅ get all filters from zustand
  const {
    openEnquiriesModal,
    endDate,
    stateId,
    categoryId,
    ageFrom,
    ageTo,
    districtId,
    gender,
    governmentType,
    setSchemeKeyword,
    setOpenEnquiriesModal,
    setStartDate,
    setEndDate,
    setStateId,
    setCategoryId,
    setAgeTo,
    setGender,
    setDistrictId,
    setAgeFrom,
    setGovernmentType,
    resetFilter,
    setApplyFilter
  } = useFilterEnquiriesStore();

  const [_endDate, _setEndDate] = useState<string>(endDate);
  const [_startDate, _setStartDate] = useState<string>(endDate);
  const [_schemeKeyword, _setSchemeKeyword] = useState("");
  const [_stateId, _setStateId] = useState<number | null >(stateId);
  const [_categoryId, _setCategoryId] = useState<number | null>(categoryId);
  const [_ageFrom, _setAgeFrom] = useState<number | null>(ageFrom);
  const [_ageTo, _setAgeTo] = useState<number | null>(ageTo);
  const [_districtId, _setDistrictId] = useState<number | null>(districtId);
  const [_selectedGender, _setSelectedGender] = useState<number | null>(gender);
  const [_governmentType, _setGovernmentType] = useState<number | null>(governmentType);

  useEffect(() => {
    if (_endDate != null && _startDate != null && _startDate > _endDate) {
      _setEndDate("");
    }
  }, [_startDate]);

  const { data: district, isLoading: districtLoading } = useQuery(["districtList", _stateId], async () => {
    if (_stateId) {
      let response = await getDistrictMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });

  const handleClose = () => setOpenEnquiriesModal(false);

  return (
    <Dialog open={openEnquiriesModal} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Card elevation={0} sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Enquiries
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 3, pt: 2 }}>
          <Typography variant="body2" fontWeight={700} color={black}>
            Filter By
          </Typography>
          <Button
            variant="text"
            sx={{ p: 0, color: purple, textTransform: "none", fontWeight: 600 }}
            onClick={() => {
              resetFilter();
              _setEndDate("");
              _setStartDate("");
              _setAgeFrom(null);
              _setCategoryId(null)
              _setAgeTo(null);
              _setSelectedGender(null)
              _setStateId(null);
              _setDistrictId(null);
              _setGovernmentType(null)
              setOpenEnquiriesModal(false);
            }}
          >
            Reset
          </Button>
        </Box>

        <CardContent sx={{ maxHeight: 400, overflow: "auto scroll", px: 3 }}>
          <Typography fontSize={14} fontWeight={600} color={black}>
            Scheme
          </Typography>
          <TextField
            value={_schemeKeyword}
            onChange={(e) => _setSchemeKeyword(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            label={"Search for scheme"}
            placeholder={"Search for scheme"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <GridSearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1 }}
          />
          <Divider sx={{ mt: 2, mb: 1 }} />
          {_dropdownOption({
            title: "Category",
            placeholder: "Search for Category",
            options: category,
            selectedId: _categoryId,
            disabled: loading,
            onSelect: (id) => {
              _setCategoryId(id);
            },
          })}
          {_dropdownOption({
            title: "Government Type",
            placeholder: "Search for Government Type",
            options: govermentList,
            selectedId: _governmentType,
            disabled: loading,
            onSelect: (id) => {
              _setGovernmentType(id);
            },
          })}
          {_dropdownOption({
            title: "State",
            placeholder: "Search for State",
            options: states,
            selectedId: _stateId,
            disabled: loading,
            onSelect: (id) => {
              _setStateId(id);
              _setDistrictId(null)
            },
          })}
          {_dropdownOption({
            title: "District",
            placeholder: "Select District",
            selectedId: _districtId,
            disabled: !_stateId || districtLoading,
            onSelect: (id) => {
              _setDistrictId(id);
            },
            options:
              district?.map((city: any) => {
                return { id: city.id, value: city?.value };
              }) || [],
          })}
          {_dateRange({
            title: "Date Range",
            setEndDate: _setEndDate,
            setStartDate: _setStartDate,
            startDate: _startDate,
            endDate: _endDate,
          })}
          <Typography mt={2} fontSize={14} fontWeight={600} color={black} mb={1}>
            Gender
          </Typography>
          <Box>
            {genderList?.map((gender) => {
              const selected = _selectedGender === gender.id;
              return (
                <Button
                  key={gender?.id}
                  disabled={loading}
                  onClick={() => {
                    if (_selectedGender === gender?.id) {
                      _setSelectedGender(null);
                    } else {
                      _setSelectedGender(gender?.id);
                    }
                  }}
                  variant={selected ? "contained" : "outlined"}
                  disableElevation={selected}
                  sx={
                    selected
                      ? {
                          mr: 1,
                          mb: 1,
                          textTransform: "none",
                          fontWeight: 600,
                          bgcolor: purple,
                          "&:hover": { bgcolor: "#7A2074" },
                        }
                      : {
                          mr: 1,
                          mb: 1,
                          textTransform: "none",
                          fontWeight: 600,
                          borderColor: "#ABA0AB",
                          color: black,
                          "&:hover": { borderColor: purple, bgcolor: purpleLight },
                        }
                  }
                >
                  {gender?.value}
                </Button>
              );
            })}
          </Box>
          <Box mt={2} display="flex" justifyContent="space-between" gap={1.5}>
            <Button
              onClick={handleClose}
              variant="outlined"
              fullWidth
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
              onClick={() => {
                setStartDate(_startDate);
                setEndDate(_endDate);
                setSchemeKeyword(_schemeKeyword);
                setCategoryId(_categoryId);
                setStateId(_stateId);
                setOpenEnquiriesModal(false);
                setAgeFrom(_ageFrom);
                setAgeTo(_ageTo);
                setGender(_selectedGender);
                setDistrictId(_districtId);
                setGovernmentType(_governmentType)
                setApplyFilter(true)
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Dialog>
  );

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
    disabled = false,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
    disabled?: boolean;
  }) {
    return (
      <Box pt={2} pb={1}>
        <Typography fontSize={14} fontWeight={600} color={black}>
          {title}
        </Typography>
        <Box pt={1} pb={2} display="flex" flexDirection="row">
          <Autocomplete
            fullWidth
            size="small"
            disabled={disabled}
            loading={disabled}
            value={options.find((option) => selectedId === option?.id) || null}
            onChange={(e, value) => {
              onSelect(value?.id || null);
            }}
            options={options}
            getOptionLabel={(option) => option?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={placeholder}
                placeholder={disabled ? "Loading options..." : placeholder}
              />
            )}
          />
        </Box>
        <Divider />
      </Box>
    );
  }

  function _dateRange({
    title,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  }: {
    title: string;
    startDate: string;
    setStartDate: (value: string) => void;
    endDate: string;
    setEndDate: (value: string) => void;
  }) {
    return (
      <Box pt={2} pb={1}>
        <Typography fontSize={14} fontWeight={600} color={black}>
          {title}
        </Typography>
        <Box pt={1} pb={2} display="flex" flexDirection={"column"} gap={2}>
          <DatePicker
            label="Start Date"
            format="DD-MM-YYYY"
            disableFuture
            value={startDate ? dayjs(startDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setStartDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: { elevation: 3 }, textField: { size: "small" } }}
            sx={{
              "& .MuiSvgIcon-root": {
                fill: purple,
              },
            }}
          />
          <DatePicker
            label="End Date"
            format="DD-MM-YYYY"
            disableFuture
            value={endDate ? dayjs(endDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setEndDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: { elevation: 3 }, textField: { size: "small" } }}
            minDate={dayjs(startDate, "YYYY-MM-DD")}
            sx={{
              "& .MuiSvgIcon-root": {
                fill: purple,
              },
            }}
          />
        </Box>
      </Box>
    );
  }
}
